import { endOfToday, startOfYear } from 'date-fns';
import React from 'react';
import { usePersistedQueryParams } from '../../../lib/hooks/use-persisted-query-params.ts';
import { useRequiredContext } from '../../../lib/hooks/use-required-context.ts';
import type { QueryParamsObjectConfig } from '../../../lib/query-params.ts';
import {
    dateParam,
    enumParam,
    generateQueryParamsString,
    numberParam,
    objectParam,
    stringArrayParam,
    stringParam,
} from '../../../lib/query-params.ts';
import type {
    CustomDateRange,
    ExplorerSort,
    ExplorerViewFilters,
} from '../../../server/utils/explorer-utils.ts';
import {
    BillingStatus,
    ExplorerSortBy,
    ExplorerSortOrder,
    ExplorerTopLevelEntity,
    TargetUnit,
    VisibilityWindow,
} from '../../../server/utils/explorer-utils.ts';

export type ExplorerState = {
    tab: ExplorerTopLevelEntity;
    searchString: string;
    searchStringCustomer: string;
    searchStringClerk: string;
    searchStringIndustry: string;
    searchStringProduct: string;
    searchStringProductGroup: string;
    searchStringStorageLocation: string;
    searchStringSupplier: string;
    searchStringSalesRep: string;
    searchStringNormalisedCategory: string;
    window: VisibilityWindow;
    billing: BillingStatus;
    targetUnit: TargetUnit;
    sorting: NonNullable<ExplorerSort>;
    customDateRange: NonNullable<CustomDateRange>;
    yearPeriods: number;
} & Required<ExplorerViewFilters>;

const explorerQueryParamsConfig = {
    tab: enumParam(ExplorerTopLevelEntity),
    searchString: stringParam(),
    searchStringCustomer: stringParam(),
    searchStringClerk: stringParam(),
    searchStringIndustry: stringParam(),
    searchStringProduct: stringParam(),
    searchStringProductGroup: stringParam(),
    searchStringStorageLocation: stringParam(),
    searchStringSupplier: stringParam(),
    searchStringSalesRep: stringParam(),
    searchStringNormalisedCategory: stringParam(),
    window: enumParam(VisibilityWindow),
    customDateRange: objectParam<NonNullable<CustomDateRange>>({
        from: dateParam(),
        to: dateParam(),
    }),
    billing: enumParam(BillingStatus),
    targetUnit: enumParam(TargetUnit),
    sorting: objectParam<NonNullable<ExplorerSort>>({
        field: enumParam(ExplorerSortBy),
        direction: enumParam(ExplorerSortOrder),
    }),
    yearPeriods: numberParam(),
    // filters
    customer: stringArrayParam(),
    clerk: stringArrayParam(),
    industry: stringArrayParam(),
    product: stringArrayParam(),
    productGroup: stringArrayParam(),
    storageLocation: stringArrayParam(),
    supplier: stringArrayParam(),
    salesRep: stringArrayParam(),
    normalisedCategory: stringArrayParam(),
} satisfies QueryParamsObjectConfig<ExplorerState>;

export const defaultExplorerFilters: ExplorerViewFilters = {
    customer: [],
    clerk: [],
    industry: [],
    product: [],
    productGroup: [],
    storageLocation: [],
    supplier: [],
    salesRep: [],
    normalisedCategory: [],
};

export const initialExplorerState = {
    ...defaultExplorerFilters,
    tab: ExplorerTopLevelEntity.STORAGE_LOCATION,
    searchString: '',
    searchStringCustomer: '',
    searchStringClerk: '',
    searchStringIndustry: '',
    searchStringProduct: '',
    searchStringProductGroup: '',
    searchStringStorageLocation: '',
    searchStringSupplier: '',
    searchStringSalesRep: '',
    searchStringNormalisedCategory: '',
    window: VisibilityWindow.YTD,
    customDateRange: {
        from: startOfYear(new Date()),
        to: endOfToday(),
    },
    billing: BillingStatus.BILLED,
    targetUnit: TargetUnit.REVENUE,
    sorting: {
        field: ExplorerSortBy.REVENUE_CURRENT,
        direction: ExplorerSortOrder.DESC,
    },
    yearPeriods: 2,
};

export const generateSearchParamsForExplorer = (params: Partial<ExplorerState>) =>
    generateQueryParamsString(explorerQueryParamsConfig, params);

// Helper function to get the search string field name for a tab
export const getSearchStringFieldForTab = (tab: ExplorerTopLevelEntity): keyof ExplorerState => {
    switch (tab) {
        case ExplorerTopLevelEntity.CUSTOMER:
            return 'searchStringCustomer';
        case ExplorerTopLevelEntity.CLERK:
            return 'searchStringClerk';
        case ExplorerTopLevelEntity.INDUSTRY:
            return 'searchStringIndustry';
        case ExplorerTopLevelEntity.PRODUCT:
            return 'searchStringProduct';
        case ExplorerTopLevelEntity.PRODUCT_GROUP:
            return 'searchStringProductGroup';
        case ExplorerTopLevelEntity.STORAGE_LOCATION:
            return 'searchStringStorageLocation';
        case ExplorerTopLevelEntity.SUPPLIER:
            return 'searchStringSupplier';
        case ExplorerTopLevelEntity.SALES_REP:
            return 'searchStringSalesRep';
        case ExplorerTopLevelEntity.NORMALISED_CATEGORY:
            return 'searchStringNormalisedCategory';
        default:
            return 'searchString';
    }
};

// Helper function to get the search string value for the current tab
export const getSearchStringForTab = (state: Partial<ExplorerState>, tab: ExplorerTopLevelEntity): string => {
    const fieldName = getSearchStringFieldForTab(tab);
    return (state[fieldName] as string) || '';
};

type UseExplorerQueryParamsProps = {
    tab?: ExplorerTopLevelEntity;
} & Partial<ExplorerViewFilters>;

export const useExplorerQueryParams = ({
    tab = ExplorerTopLevelEntity.STORAGE_LOCATION,
    ...defaultFilters
}: UseExplorerQueryParamsProps) => {
    /**
     * TODO: maybe we need to merge the defaultFilters with the initialExplorerState
     * to ensure that all filters are present in the state
     */
    return usePersistedQueryParams(
        explorerQueryParamsConfig,
        {
            ...initialExplorerState,
            ...defaultFilters,
            tab,
        },
        'explorer',
    );
};

export const useExplorerQueryFilters = (props: UseExplorerQueryParamsProps) => {
    const [state] = useExplorerQueryParams(props);
    return getExplorerQueryFilters(state);
};

export const getExplorerQueryFilters = (state: Partial<ExplorerState>) => {
    return Object.keys(defaultExplorerFilters).reduce((acc, key) => {
        acc[key as keyof ExplorerViewFilters] = state[key as keyof ExplorerViewFilters] ?? [];
        return acc;
    }, {} as ExplorerViewFilters);
};

export const ExplorerViewContext = React.createContext<{
    state: Partial<ExplorerState>;
    setState: React.Dispatch<React.SetStateAction<Partial<ExplorerState>>>;
    defaultFilters: Partial<ExplorerViewFilters>;
} | null>(null);

export const ExplorerViewProvider = ExplorerViewContext.Provider;

export const useExplorerView = () => {
    const context = useRequiredContext(ExplorerViewContext, 'ExplorerViewProvider');

    const state = {
        ...initialExplorerState,
        ...context.state,
    };

    const filters = getExplorerQueryFilters(state);

    const combinedFilters = {
        ...filters,
        ...context.defaultFilters,
    };

    return {
        ...context,
        state: {
            ...state,
            billingStatus: state.billing,
            searchString: getSearchStringForTab(state, state.tab),
        },
        /**
         * These are the filters that are applied by the user
         */
        filters,
        /**
         * These are the filters that will have the default values applied
         */
        combinedFilters,
    };
};
